import React from 'react';

const OpenClaw = () => (
  <article className="news-article-body">
    <p className="news-lead">
      OpenClaw (previously known as Clawd or Moltbot) is a community-built, open-source
      toolkit that extends Claude's capabilities via the Anthropic API. It's not affiliated
      with Anthropic - it's a grassroots developer project born from a simple frustration:
      Claude is powerful, but the raw API alone doesn't give you everything you need to
      build serious applications. OpenClaw fills the gaps.
    </p>

    <h2>What OpenClaw actually does</h2>
    <p>
      At its core, OpenClaw is a framework that wraps the Claude API with higher-level
      tools developers need for real applications:
    </p>
    <ul>
      <li><strong>Memory management:</strong> Persisting context across sessions - Claude itself has no built-in memory between conversations</li>
      <li><strong>Tool use scaffolding:</strong> A cleaner way to define and handle tools Claude can call (search, code execution, file access, etc.)</li>
      <li><strong>Multi-agent coordination:</strong> Running multiple Claude instances that can delegate tasks to each other</li>
      <li><strong>Template library:</strong> Reusable prompt patterns for common tasks</li>
      <li><strong>Evaluation framework:</strong> Tools for testing and comparing prompts systematically</li>
    </ul>

    <h2>The name change - from Clawd to OpenClaw</h2>
    <p>
      The project was originally called "Clawd" (a phonetic play on "Claude"), then briefly
      "Moltbot" (a reference to moulting - shedding constraints). It settled on "OpenClaw"
      to avoid trademark issues with Anthropic and to better reflect its open, community-driven
      nature. "Claw" as in the claw of a lobster - a nod to another AI agent project,
      LobsterAI (养龙虾), that inspired parts of the design.
    </p>

    <h2>Why the community built this</h2>
    <p>
      When Claude became available via API, developers quickly found that building anything
      beyond a simple chatbot required a lot of boilerplate: managing conversation history,
      handling rate limits, structuring tool calls, managing context window budgets. Each
      team was solving the same problems from scratch.
    </p>
    <p>
      OpenClaw emerged from shared frustration in the developer community and became the
      de facto standard toolkit for Claude-based projects among independent developers.
      It's part of a broader ecosystem of "LangChain alternatives" - lightweight, focused
      frameworks that solve specific problems rather than trying to abstract everything.
    </p>

    <h2>Anthropic's stance</h2>
    <p>
      Anthropic hasn't endorsed or opposed OpenClaw, but the project is fully within the
      Claude API terms of service. It's analogous to the thousands of projects built on
      top of OpenAI's API - unofficial, community-driven, and useful.
    </p>
    <p>
      Some of OpenClaw's design patterns have arguably influenced how developers think
      about Claude application architecture - particularly the emphasis on structured
      memory management and explicit tool permissions.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        A powerful underlying model isn't enough on its own. The developer ecosystem that
        builds around a model - the tools, frameworks, examples, and patterns - is a huge
        part of what makes it practical to use. OpenClaw is one example of how community
        tooling fills the gap between raw API access and production-ready applications.
        That ecosystem effect is one reason why CUDA-locked NVIDIA and API-locked OpenAI
        both have real moats.
      </p>
    </div>
  </article>
);

export default OpenClaw;
